import { useCallback, useEffect, useState } from 'react';
import { useToolbar } from './useToolbar';
import { dayjs } from 'utils/dayjs';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { RbInlineChipField, type RbContextMenuPosition, type RbRowSelectionModel } from '@scmlatam/web-ui';
import { Icon } from '@mui/material';
import type { Options, Position, Row, Shift } from '../types/hooks';
import type { Duration } from 'dayjs/plugin/duration';
import { usePermisoSchedule } from '../../../../auth/permisosModulos';
import justifyIcon from '../../../../assets/icons/justify_shift.svg';

const useTable = () => {
    const { t } = useTranslation('schedule.table');
    const canAddShift = usePermisoSchedule('create_shift');
    const canEditShift = usePermisoSchedule('update_shift');
    const canDeleteShift = usePermisoSchedule('delete_shift');
    const canJustifyShift = usePermisoSchedule('justify_shift');
    const canAddPaycode = usePermisoSchedule('add_paycode');
    const canEditPaycode = usePermisoSchedule('update_paycode');
    const canDeletePaycode = usePermisoSchedule('delete_paycode');

    const toolbarProps = useToolbar();

    const [shiftId, setShiftId] = useState<string | null>(null);
    const [shift, setShift] = useState<Shift | null>(null);
    const [paycodeAssignId, setPaycodeAssignId] = useState<string | null>(null);
    const [paycodeDurationId, setPaycodeDurationId] = useState<string | null>(null);

    const [cell, setCell] = useState<Position | null>(null);

    const [dateColumns, setDateColumns] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

    const getDateRange = (start: Dayjs | null, end: Dayjs | null): Dayjs[] => {
        const dates: Dayjs[] = [];
        if (!start || !end) return dates;

        let currentDate = start;

        while (currentDate.isSame(end, 'day') || currentDate.isBefore(end, 'day')) {
            dates.push(currentDate);
            currentDate = currentDate.add(1, 'day');
        }

        return dates;
    };

    const columns = [
        {
            key: 'fullname',
            header: t('columns.full_name'),
        },
        {
            key: 'planned_hours',
            header: t('columns.planned_hours'),
        },
        ...getDateRange(dateColumns[0], dateColumns[1]).map((date) => ({
            key: date?.format('YYYY-MM-DD') || '',
            header: date?.format('ddd DD/MM') || '',
            minWidth: 180,
            render: (row: Row) => (
                <div className="flex flex-col gap-1">
                    {row[date.format('YYYY-MM-DD')]?.shifts.map((shift) => (
                        <RbInlineChipField
                            type="text"
                            value={`${dayjs.utc(`${shift.date} ${shift.start_ts}`).local().format('HH:mm')} - ${dayjs.utc(`${shift.date} ${shift.end_ts}`).local().format('HH:mm')}`}
                            variant="neutral"
                            icon={
                                <Icon
                                    baseClassName="material-symbols-rounded"
                                    sx={{
                                        color: row[date.format('YYYY-MM-DD')]?.absences.find((absence) => absence.shift_id === shift.shift_id) ? '#CC002C' : undefined,
                                    }}
                                >
                                    work_history
                                </Icon>
                            }
                            editable={false}
                            actions={[
                                ...(canJustifyShift
                                    ? [
                                          {
                                              label: t('menu.justify_shift'),
                                              icon: <img src={justifyIcon} alt="" />,
                                              onClick: () => {
                                                  setOption('justify_shift');
                                                  setShiftId(shift.shift_id);
                                                  setShift(shift);
                                              },
                                          },
                                      ]
                                    : []),
                                ...(canEditShift
                                    ? [
                                          {
                                              label: t('menu.edit_shift'),
                                              icon: <Icon baseClassName="material-symbols-rounded">edit_square</Icon>,
                                              onClick: () => {
                                                  setOption('edit_shift');
                                                  setShiftId(shift.shift_id);
                                                  setShift(shift);
                                              },
                                          },
                                      ]
                                    : []),
                                ...(canDeleteShift
                                    ? [
                                          {
                                              label: t('menu.delete_shift'),
                                              icon: <Icon baseClassName="material-symbols-rounded">delete</Icon>,
                                              danger: true,
                                              onClick: () => {
                                                  setOption('delete_shift');
                                                  setShiftId(shift.shift_id);
                                                  setShift(shift);
                                              },
                                          },
                                      ]
                                    : []),
                            ]}
                            onClick={() => undefined}
                            className="w-auto min-w-[10rem] max-w-none"
                        />
                    ))}
                    {row[date.format('YYYY-MM-DD')]?.paycodeAssign.map((paycode) => {
                        // console.log('[Schedule] Código de pago asignado:', paycode);

                        return (
                            <RbInlineChipField
                                type="text"
                                value={`${paycode.amount_day} ${paycode.paycode.name} [${dayjs(`${paycode.date} ${paycode.amount}`).format('HH:mm')}]`}
                                variant="neutral"
                                icon={
                                    <Icon
                                        baseClassName="material-symbols-rounded"
                                        sx={{
                                            color: '#CB01BE',
                                        }}
                                    >
                                        wallet
                                    </Icon>
                                }
                                editable={false}
                                actions={[
                                    ...(canEditPaycode
                                        ? [
                                              {
                                                  label: t('menu.edit_paycode'),
                                                  icon: <Icon baseClassName="material-symbols-rounded">edit_square</Icon>,
                                                  onClick: () => {
                                                      setOption('edit_paycode_assign');
                                                      setPaycodeAssignId(paycode.id.toString());
                                                      setPaycodeDurationId(null);
                                                  },
                                              },
                                          ]
                                        : []),
                                    ...(canDeletePaycode
                                        ? [
                                              {
                                                  label: t('menu.delete_paycode'),
                                                  icon: <Icon baseClassName="material-symbols-rounded">delete</Icon>,
                                                  danger: true,
                                                  onClick: () => {
                                                      setOption('delete_paycode_assign');
                                                      setPaycodeAssignId(paycode.id.toString());
                                                      setPaycodeDurationId(null);
                                                  },
                                              },
                                          ]
                                        : []),
                                ]}
                                onClick={() => undefined}
                                className="w-auto min-w-[10rem] max-w-none"
                            />
                        );
                    })}
                    {row[date.format('YYYY-MM-DD')]?.paycodeDuration.map((paycode) => {
                        // console.log('[Schedule] Código de pago por duración:', paycode);

                        return (
                            <RbInlineChipField
                                type="text"
                                value={`${paycode.amount.substring(0, 5)} ${paycode.paycode.name}`}
                                variant="neutral"
                                icon={
                                    <Icon
                                        baseClassName="material-symbols-rounded"
                                        sx={{
                                            color: '#CB01BE',
                                        }}
                                    >
                                        wallet
                                    </Icon>
                                }
                                editable={false}
                                actions={[
                                    ...(canEditPaycode
                                        ? [
                                              {
                                                  label: t('menu.edit_paycode'),
                                                  icon: <Icon baseClassName="material-symbols-rounded">edit_square</Icon>,
                                                  onClick: () => {
                                                      setOption('edit_paycode_assign');
                                                      setPaycodeAssignId(null);

                                                      setPaycodeDurationId(paycode.durationpaycode_r_employee_id);
                                                  },
                                              },
                                          ]
                                        : []),
                                    ...(canDeletePaycode
                                        ? [
                                              {
                                                  label: t('menu.delete_paycode'),
                                                  icon: <Icon baseClassName="material-symbols-rounded">delete</Icon>,
                                                  danger: true,
                                                  onClick: () => {
                                                      setOption('delete_paycode_assign');
                                                      setPaycodeAssignId(null);

                                                      setPaycodeDurationId(paycode.durationpaycode_r_employee_id);
                                                  },
                                              },
                                          ]
                                        : []),
                                ]}
                                onClick={() => undefined}
                                className="w-auto min-w-[10rem] max-w-none"
                            />
                        );
                    })}
                </div>
            ),
        })),
    ];

    const [contextMenu, setContextMenu] = useState<RbContextMenuPosition | null>(null);

    const [option, setOption] = useState<Options | null>(null);

    const [rowId, setRowId] = useState<string | null>(null);
    const [rows, setRows] = useState<Row[]>([]);
    const [rowSelected, setRowSelected] = useState<RbRowSelectionModel>({
        ids: new Set(),
        type: 'include',
    });

    const selectedIds = !Array.isArray(rowSelected) && rowSelected && typeof rowSelected === 'object' && 'ids' in rowSelected ? rowSelected.ids : rowSelected;

    const selectedIdsSet = new Set(selectedIds instanceof Set ? Array.from(selectedIds) : Array.isArray(selectedIds) ? selectedIds : []);

    const selectedRowIds = selectedIdsSet.size > 0 ? selectedIdsSet : rowId ? new Set([rowId]) : new Set<string>();

    const items = [
        ...(canAddShift
            ? [
                  {
                      id: 'schedule-context-add-shift',
                      label: t('menu.add_shift'),
                      icon: <Icon baseClassName="material-symbols-rounded">add</Icon>,
                      onClick: () => {
                          setOption('add_shift');
                      },
                  },
              ]
            : []),
        ...(canAddPaycode
            ? [
                  {
                      id: 'schedule-context-add-paycode',
                      label: t('menu.add_paycode'),
                      icon: <Icon baseClassName="material-symbols-rounded">add</Icon>,
                      onClick: () => {
                          setOption('add_paycode_assign');
                      },
                  },
              ]
            : []),
    ];

    const formatTime = (thw: Duration, tpa: Duration, tpd: Duration) => {
        const hours = Math.floor(thw.asHours()).toString().padStart(2, '0');
        const minutes = String(thw.minutes()).padStart(2, '0');
        //const seconds = String(duration.seconds()).padStart(2, "0");

        //totalDutationByPaycodes
        const tdbp = tpa.add(tpd);

        const tdbpHours = Math.floor(tdbp.asHours()).toString().padStart(2, '0');
        const tdbpMinutes = String(tdbp.minutes()).padStart(2, '0');

        return `${hours}:${minutes} | ${tdbpHours}:${tdbpMinutes}`;
    };

    const generateRows = useCallback(() => {
        const rows = toolbarProps.employees
            .filter((employee) => toolbarProps.employeeSelected.includes(employee.value.toString()))
            .map((employee) => {
                const row = {} as Row;

                row.id = employee.value.toString();
                row.fullname = employee.label;
                row.acumulation_by_paycode_assign = dayjs.duration({
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                row.acumulation_by_planning = dayjs.duration({
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                row.acumulation_by_paycode_duration = dayjs.duration({
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                row.planned_hours = '00:00 | 00:00';

                try {
                    for (const date of getDateRange(toolbarProps.period[0], toolbarProps.period[1])) {
                        try {
                            const dateKey = date.format('YYYY-MM-DD');
                            row[dateKey] = {
                                shifts: toolbarProps.shifts.filter((shift) => String(shift.employee_id) === String(employee.value) && shift.date === dateKey),
                                paycodeAssign: toolbarProps.paycodeAssigns.filter((paycode) => String(paycode.id_employee) === String(employee.value) && paycode.date === dateKey),
                                paycodeDuration: toolbarProps.paycodeDuration.filter((paycode) => String(paycode.employee_id) === String(employee.value) && paycode.date_timecard === dateKey),
                                absences: toolbarProps.absences.filter((absence) => String(absence.employee_id) === String(employee.value) && absence.date === dateKey),
                                hours_by_planning: toolbarProps.shifts
                                    .filter((shift) => String(shift.employee_id) === String(employee.value) && shift.date === dateKey)
                                    .map((shift) => shift.duration.substring(0, 5)),
                                hours_by_paycode_assign: toolbarProps.paycodeAssigns
                                    .filter((paycode) => String(paycode.id_employee) === String(employee.value) && paycode.date === dateKey)
                                    .map((paycode) => paycode.amount),
                                hours_by_paycode_duration: toolbarProps.paycodeDuration
                                    .filter((paycode) => String(paycode.employee_id) === String(employee.value) && paycode.date_timecard === dateKey)
                                    .map((paycode) => paycode.amount.substring(0, 5)),
                            };

                            const thw: Duration = row[dateKey].hours_by_planning.reduce(
                                (total, time) => {
                                    const [hours, minutes, seconds] = time.split(':').map(Number);
                                    return total.add(dayjs.duration({ hours, minutes, seconds }));
                                },
                                dayjs.duration({ hours: 0, minutes: 0, seconds: 0 }),
                            );

                            const tpa: Duration = row[dateKey].hours_by_paycode_assign.reduce(
                                (total, time) => {
                                    const [hours, minutes, seconds] = time.split(':').map(Number);
                                    return total.add(dayjs.duration({ hours, minutes, seconds }));
                                },
                                dayjs.duration({ hours: 0, minutes: 0, seconds: 0 }),
                            );

                            const tpd: Duration = row[dateKey].hours_by_paycode_duration.reduce(
                                (total, time) => {
                                    const [hours, minutes, seconds] = time.split(':').map(Number);
                                    return total.add(dayjs.duration({ hours, minutes, seconds }));
                                },
                                dayjs.duration({ hours: 0, minutes: 0, seconds: 0 }),
                            );

                            row.acumulation_by_planning = row.acumulation_by_planning.add(thw);

                            row.acumulation_by_paycode_assign = row.acumulation_by_paycode_assign.add(tpa);

                            row.acumulation_by_paycode_duration = row.acumulation_by_paycode_duration.add(tpd);
                        } catch (error) {
                            console.error(`Error processing date ${date.format('YYYY-MM-DD')} for employee ${employee.label}:`, error);
                        }
                    }
                } catch (error) {
                    console.error('Error generating rows:', error);
                }

                row.planned_hours = formatTime(row.acumulation_by_planning, row.acumulation_by_paycode_assign, row.acumulation_by_paycode_duration);

                return row;
            });

        // console.log('Generated rows:', rows);
        setRows(rows);

        setDateColumns(toolbarProps.period);

        return rows;
    }, [toolbarProps]);

    useEffect(() => {
        if (toolbarProps.loading === false) {
            generateRows();
        }
    }, [toolbarProps.loading]);

    return {
        ...toolbarProps,
        columns,
        rows,
        rowSelected,
        setRowSelected,
        selectedRowIds,
        rowId,
        setRowId,
        shiftId,
        setShiftId,
        paycodeAssignId,
        setPaycodeAssignId,
        paycodeDurationId,
        setPaycodeDurationId,
        option,
        setOption,
        items,
        contextMenu,
        setContextMenu,
        cell,
        setCell,
        shift,
        setShift,
    };
};

export { useTable };
